import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import useToast from 'contexts/ToastProvider/useToast';
import { getOtkCountInWindow, OTK_ERROR_EVENT, recordOtkError } from 'modules/Authentication/cryptoErrorMonitor';
import useMxCookies from 'modules/Authentication/hooks/useMxCookies';
import { emitSessionLifecycleEvent, getRouteClass } from 'modules/Authentication/sessionObservability';

const isActiveRtcPath = (pathname: string): boolean =>
  pathname.startsWith('/rooms/') || pathname.startsWith('/meeting');

/**
 * AC-013: when OTK upload errors repeat past the threshold within the window, show a single
 * info toast. Listens for the telemetry-dispatched `OTK_ERROR_EVENT` and never triggers any
 * auth side effect — the user stays logged in.
 */
const useCryptoErrorToast = () => {
  const { pushToast } = useToast();
  const { t } = useTranslation();
  const { mxDeviceId } = useMxCookies();
  const { pathname } = useLocation();
  // Guard so the toast shows once per threshold crossing, not on every subsequent error.
  const hasShownRef = useRef(false);

  useEffect(() => {
    const handleOtkError = () => {
      const thresholdReached = recordOtkError();
      if (!thresholdReached) {
        // Window emptied below the threshold — re-arm for the next crossing.
        hasShownRef.current = false;
        return;
      }
      if (hasShownRef.current) return;

      hasShownRef.current = true;
      emitSessionLifecycleEvent({
        domain: 'crypto',
        action: 'otk_threshold',
        scenario: 'B',
        threshold_crossed: true,
        otk_count_in_window: getOtkCountInWindow(),
        route_class: getRouteClass(pathname),
        active_rtc: isActiveRtcPath(pathname),
        mx_device_id: mxDeviceId,
      });
      pushToast({
        variant: 'info',
        title: t('encryption_sync_retry_title'),
        description: t('encryption_sync_retry_description'),
        autoDismiss: true,
      });
    };

    window.addEventListener(OTK_ERROR_EVENT, handleOtkError);
    return () => window.removeEventListener(OTK_ERROR_EVENT, handleOtkError);
  }, [mxDeviceId, pathname, pushToast, t]);
};

export default useCryptoErrorToast;
